"use client";

// Change-password FORM (C4). Page wiring is deferred to a later slice — the
// dashboard shell it will live in doesn't exist yet — so this is the
// standalone, reusable form the settings page mounts once it lands.
//
// Error rendering mirrors the login page's pattern (src/app/(auth)/login/
// page.tsx): AuthApiError.fieldErrors -> per-field message wired through
// aria-describedby, else AuthApiError.detail -> a top alert banner. The
// module's two measured change-password fixtures
// (contract-fixtures/scopely/auth/change-password/{success,wrong-current-
// password}.json) only exercise the `detail` branch — wrong-current-password
// is a plain 400 with no field_errors — so the field-error branch below is
// forward-compatible plumbing (same mechanism as login's empty-body-
// validation case), not something either fixture drives. FIXTURE GAP noted
// in the module README.
import { useState, type FormEvent } from "react";

import {
  authApi,
  AuthApiError,
  MIN_PASSWORD_LENGTH,
  PasswordRequirements,
  useUser,
  type ChangePasswordResponse,
} from "@/modules/auth";
import { Button, PasswordInput } from "@/shared/ui";

export interface ChangePasswordFormProps {
  onSuccess?: (result: ChangePasswordResponse) => void;
}

export function ChangePasswordForm({ onSuccess }: ChangePasswordFormProps) {
  // Identity for PasswordRequirements' resemblance check — available once
  // this form is mounted inside an authenticated route (AuthProvider
  // ancestor); null pre-mount/pre-session is handled the same way the login
  // page handles a pending reset user (analyzePasswordPolicy treats a
  // missing identity as "no hints", not an error).
  const user = useUser();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors(null);
    setSuccessMessage(null);
    try {
      if (newPassword !== confirmPassword) {
        setError("New passwords do not match");
        setLoading(false);
        return;
      }
      const result = await authApi.changePassword(currentPassword, newPassword);
      setSuccessMessage(result.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onSuccess?.(result);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.fieldErrors) {
          setFieldErrors(err.fieldErrors);
        } else {
          setError(err.detail ?? "Could not change password. Please try again.");
        }
      } else {
        setError(err instanceof Error ? err.message : "Could not change password. Please try again.");
      }
      setLoading(false);
      return;
    }
    setLoading(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          role="status"
          className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
        >
          {successMessage}
        </div>
      )}

      <div>
        <label htmlFor="change-password-current" className="mb-1 block text-sm font-medium text-ink">
          Current Password
        </label>
        <PasswordInput
          id="change-password-current"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          aria-invalid={Boolean(fieldErrors?.current_password) || undefined}
          aria-describedby={fieldErrors?.current_password ? "change-password-current-error" : undefined}
          className={`h-10 w-full rounded-lg border bg-surface px-3 text-sm text-ink placeholder:text-ink-faint ${
            fieldErrors?.current_password ? "border-red-500" : "border-edge"
          }`}
        />
        {fieldErrors?.current_password && (
          <p id="change-password-current-error" className="mt-1 text-xs text-red-600">
            {fieldErrors.current_password.join(" ")}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="change-password-new" className="mb-1 block text-sm font-medium text-ink">
          New Password
        </label>
        <PasswordInput
          id="change-password-new"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={MIN_PASSWORD_LENGTH}
          aria-invalid={Boolean(fieldErrors?.new_password) || undefined}
          aria-describedby={fieldErrors?.new_password ? "change-password-new-error" : undefined}
          className={`h-10 w-full rounded-lg border bg-surface px-3 text-sm text-ink placeholder:text-ink-faint ${
            fieldErrors?.new_password ? "border-red-500" : "border-edge"
          }`}
        />
        {fieldErrors?.new_password && (
          <p id="change-password-new-error" className="mt-1 text-xs text-red-600">
            {fieldErrors.new_password.join(" ")}
          </p>
        )}
        <PasswordRequirements
          password={newPassword}
          email={user?.email}
          username={user?.username}
          firstName={user?.first_name}
          lastName={user?.last_name}
          className="mt-2 space-y-1"
        />
      </div>

      <div>
        <label htmlFor="change-password-confirm" className="mb-1 block text-sm font-medium text-ink">
          Confirm New Password
        </label>
        <PasswordInput
          id="change-password-confirm"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={MIN_PASSWORD_LENGTH}
          className="h-10 w-full rounded-lg border border-edge bg-surface px-3 text-sm text-ink placeholder:text-ink-faint"
        />
      </div>

      <Button type="submit" loading={loading}>
        {loading ? "Changing Password..." : "Change Password"}
      </Button>
    </form>
  );
}
